import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { ExternalLink, Menu, ShoppingBag, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { BrandMark } from '../components/BrandMark'
import { CartDrawer } from '../components/CartDrawer'
import { ProductQuickShop } from '../components/ProductQuickShop'
import {
  CartReassurance,
  DeliveryCurtainCall,
  FounderTrust,
  PostcodePromiseBand,
  StarsOfShow,
  TreatFaq,
  TreatHero,
} from '../components/color-impact/ColorImpactSections'
import { CreateBucketExperience } from '../experience/create-bucket/CreateBucketExperience'
import { bucketToCartLine } from '../experience/create-bucket/bucketCartAdapter'
import type { BucketComposition } from '../experience/create-bucket/bucket.types'
import { formatCurrency } from '../lib/money'
import { useRevealMotion } from '../motion/useRevealMotion'
import type {
  CartLine,
  DeliveryChoice,
  MysteryBucketPlanAction,
  MysteryBucketService,
  Product,
} from '../types'
import '../styles/colour-impact.css'

const DAY_MS = 24 * 60 * 60 * 1000

function cadenceStepMs(cadence: MysteryBucketService['cadence']) {
  return (cadence === 'fortnightly' ? 14 : 7) * DAY_MS
}

function formatDeliveryDate(date: Date) {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Australia/Brisbane',
  }).format(date)
}

function formatCutoffLabel(date: Date) {
  return `${new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Australia/Brisbane',
  }).format(date)}, 12:00 pm AEST`
}

function advanceRecurringService(
  service: MysteryBucketService,
): MysteryBucketService {
  const stepMs = cadenceStepMs(service.cadence)
  const firstDrop = new Date(new Date(service.firstDropIso).getTime() + stepMs)
  const cutoff = new Date(new Date(service.cutoffIso).getTime() + stepMs)

  return {
    ...service,
    firstDropIso: firstDrop.toISOString(),
    cutoffIso: cutoff.toISOString(),
    cutoffLabel: formatCutoffLabel(cutoff),
    delivery: {
      ...service.delivery,
      date: formatDeliveryDate(firstDrop),
    },
  }
}

function rollServicePastCutoff(
  service: MysteryBucketService,
  now: Date,
): MysteryBucketService {
  let next = service
  let guard = 0

  while (now.getTime() >= new Date(next.cutoffIso).getTime() && guard < 104) {
    next = advanceRecurringService(next)
    guard += 1
  }

  return next
}

function withUpdatedService(
  line: CartLine,
  service: MysteryBucketService,
): CartLine {
  const profileName = line.detail?.split(' · First drop')[0] ?? line.name

  return {
    ...line,
    service,
    detail: `${profileName} · First drop ${service.delivery.day} ${service.delivery.date}, ${service.delivery.window}`,
  }
}

export function isMysteryPlanActionOpen(
  service: MysteryBucketService,
  action: MysteryBucketPlanAction,
  now: Date,
): boolean {
  if (action === 'resume') return true

  const cutoffIso =
    action === 'undo_skip' && service.skippedDrop
      ? service.skippedDrop.cutoffIso
      : service.cutoffIso
  return now.getTime() < new Date(cutoffIso).getTime()
}

export function ShopPage() {
  const reduceMotion = useReducedMotion()
  const appRef = useRef<HTMLDivElement>(null)
  const toastTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null)
  const [cart, setCart] = useState<CartLine[]>([])
  const [cartOpen, setCartOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [quickShopProduct, setQuickShopProduct] = useState<Product | null>(null)
  const [selectedDelivery, setSelectedDelivery] =
    useState<DeliveryChoice | null>(null)
  const [serviceCheckoutComplete, setServiceCheckoutComplete] = useState(false)
  const [managedPlanService, setManagedPlanService] =
    useState<MysteryBucketService | null>(null)
  const mysteryPlanLine =
    cart.find((line) => line.kind === 'mystery-plan') ?? null

  const cartQuantity = useMemo(
    () => cart.reduce((sum, line) => sum + line.quantity, 0),
    [cart],
  )
  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, line) => sum + line.priceCents * line.quantity,
        0,
      ),
    [cart],
  )
  const productQuantities = useMemo(
    () =>
      cart.reduce<Record<string, number>>((quantities, line) => {
        quantities[line.key] = line.quantity
        return quantities
      }, {}),
    [cart],
  )

  const showToast = useCallback((message: string) => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current)
    }

    setToast(message)
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null
      setToast('')
    }, 2200)
  }, [])

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current)
      toastTimerRef.current = null
    }
    setToast('')
  }, [])

  const openCart = useCallback(() => {
    dismissToast()
    setCartOpen(true)
  }, [dismissToast])

  useEffect(
    () => () => {
      if (toastTimerRef.current !== null) {
        window.clearTimeout(toastTimerRef.current)
        toastTimerRef.current = null
      }
    },
    [],
  )

  useEffect(() => {
    const targetId = window.location.hash.slice(1)
    if (!targetId) return

    const timer = window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: 'start' })
    }, 220)

    return () => window.clearTimeout(timer)
  }, [])

  const addLine = useCallback(
    (line: Omit<CartLine, 'quantity'>) => {
      setCart((current) => {
        const existing = current.find((item) => item.key === line.key)
        if (existing) {
          return current.map((item) =>
            item.key === line.key
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          )
        }
        return [...current, { ...line, quantity: 1 }]
      })
      showToast(`${line.name} added`)
    },
    [showToast],
  )

  const addProduct = useCallback(
    (product: Product) =>
      addLine({
        key: product.id,
        name: product.name,
        priceCents: product.priceCents,
        image: product.image,
      }),
    [addLine],
  )

  const addBucket = useCallback(
    (
      composition: BucketComposition,
      service?: MysteryBucketService,
    ) => {
      if (!service) {
        addLine(bucketToCartLine(composition))
        return
      }

      const line = bucketToCartLine(composition, service)
      setCart((current) => [
        ...current.filter((item) => item.kind !== 'mystery-plan'),
        { ...line, quantity: 1 },
      ])
      setManagedPlanService(service)
      setSelectedDelivery(service.delivery)
      setServiceCheckoutComplete(false)
      showToast(
        service.cadence === 'once'
          ? `${line.name} added with local delivery`
          : `${line.name} added at the per-delivery price`,
      )
    },
    [addLine, showToast],
  )

  const removeMysteryPlan = useCallback(() => {
    setCart((current) =>
      current.filter((line) => line.kind !== 'mystery-plan'),
    )
    setSelectedDelivery(null)
    setServiceCheckoutComplete(false)
    setManagedPlanService(null)
    showToast('Mystery Bucket removed from the demo cart')
  }, [showToast])

  const applyManagedPlanService = useCallback(
    (service: MysteryBucketService, removeFromCart = false) => {
      setManagedPlanService(service)
      setCart((current) =>
        removeFromCart
          ? current.filter((line) => line.kind !== 'mystery-plan')
          : current.map((line) =>
              line.kind === 'mystery-plan'
                ? withUpdatedService(line, service)
                : line,
            ),
      )
      setSelectedDelivery(removeFromCart ? null : service.delivery)
    },
    [],
  )

  const manageMysteryPlan = useCallback(
    (action: MysteryBucketPlanAction) => {
      const current = managedPlanService
      if (
        !current ||
        current.cadence === 'once' ||
        current.lifecycleStatus === 'cancelled'
      ) {
        return
      }

      const now = new Date()
      const changesOpen = isMysteryPlanActionOpen(current, action, now)

      if (!changesOpen) {
        showToast(
          action === 'undo_skip'
            ? 'That skipped drop is now locked'
            : 'That drop has passed its change cut-off',
        )
        return
      }

      if (action === 'skip') {
        if (current.lifecycleStatus === 'paused' || current.skippedDrop) return

        const next = advanceRecurringService(current)
        applyManagedPlanService({
          ...next,
          skippedDrop: {
            firstDropIso: current.firstDropIso,
            cutoffIso: current.cutoffIso,
            cutoffLabel: current.cutoffLabel,
            delivery: current.delivery,
          },
        })
        showToast('Next Mystery Bucket skipped')
        return
      }

      if (action === 'undo_skip') {
        if (!current.skippedDrop || current.lifecycleStatus === 'paused') return
        const skippedDrop = current.skippedDrop
        const { skippedDrop: _removed, ...restored } = current
        applyManagedPlanService({
          ...restored,
          firstDropIso: skippedDrop.firstDropIso,
          cutoffIso: skippedDrop.cutoffIso,
          cutoffLabel: skippedDrop.cutoffLabel,
          delivery: skippedDrop.delivery,
        })
        showToast('Next Mystery Bucket restored')
        return
      }

      if (action === 'pause') {
        if (current.lifecycleStatus !== 'active') return
        applyManagedPlanService({
          ...current,
          lifecycleStatus: 'paused',
        })
        showToast('Mystery Bucket deliveries paused')
        return
      }

      if (action === 'resume') {
        if (current.lifecycleStatus !== 'paused') return
        const {
          skippedDrop: _expiredSkip,
          ...withoutExpiredSkip
        } = current
        const next =
          now.getTime() >= new Date(current.cutoffIso).getTime()
            ? rollServicePastCutoff(withoutExpiredSkip, now)
            : current
        applyManagedPlanService({
          ...next,
          lifecycleStatus: 'active',
        })
        showToast('Mystery Bucket deliveries resumed')
        return
      }

      applyManagedPlanService(
        {
          ...current,
          lifecycleStatus: 'cancelled',
        },
        true,
      )
      showToast('Mystery Bucket plan cancelled')
    },
    [applyManagedPlanService, managedPlanService, showToast],
  )

  const clearCart = useCallback(() => {
    setCart([])
    setSelectedDelivery(null)
    setServiceCheckoutComplete(false)
    setManagedPlanService(null)
  }, [])
  const closeQuickShop = useCallback(() => setQuickShopProduct(null), [])
  const closeCart = useCallback(() => setCartOpen(false), [])

  const changeQuantity = useCallback((key: string, delta: number) => {
    setCart((current) =>
      current
        .map((line) =>
          line.key === key
            ? { ...line, quantity: Math.max(0, line.quantity + delta) }
            : line,
        )
        .filter((line) => line.quantity > 0),
    )
  }, [])

  useRevealMotion(appRef)

  return (
    <div className="app shop-page" ref={appRef}>
      <div className="prototype-strip">
        <span className="prototype-strip__message">
          Clickable home-delivery MVP <span aria-hidden="true">·</span> No order
          or payment is created
        </span>
        <a className="prototype-strip__return" href="/">
          Return to proposal
        </a>
      </div>

      <header className="site-header treat-header">
        <a href="#top" className="brand-link" aria-label="Sub Zero Treats home">
          <BrandMark />
        </a>
        <nav className={menuOpen ? 'is-open' : ''} aria-label="Primary navigation">
          <a href="#create-a-bucket" onClick={() => setMenuOpen(false)}>
            Build a bucket
          </a>
          <a href="#stars-of-the-show" onClick={() => setMenuOpen(false)}>
            Shop treats
          </a>
          <a href="#delivery-days" onClick={() => setMenuOpen(false)}>
            Delivery days
          </a>
        </nav>
        <div className="header-actions">
          <button
            className="menu-button icon-button"
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
          <motion.button
            className="cart-button"
            type="button"
            aria-label={`Open cart, ${cartQuantity} ${
              cartQuantity === 1 ? 'item' : 'items'
            }`}
            onClick={openCart}
            whileTap={{ scale: 0.97 }}
          >
            <ShoppingBag aria-hidden="true" />
            <span>Cart</span>
            <AnimatePresence mode="popLayout">
              <motion.strong
                key={cartQuantity}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.6, opacity: 0 }}
              >
                {cartQuantity}
              </motion.strong>
            </AnimatePresence>
          </motion.button>
        </div>
      </header>

      <main id="top">
        <TreatHero
          onBrowse={() =>
            document
              .getElementById('stars-of-the-show')
              ?.scrollIntoView({
                behavior: reduceMotion ? 'auto' : 'smooth',
              })
          }
        />
        <PostcodePromiseBand />
        <div className="bucket-section-wrap">
          <CreateBucketExperience
            onAddBucket={addBucket}
            onOpenCart={openCart}
            hasMysteryPlanInCart={Boolean(mysteryPlanLine)}
            planCheckoutComplete={serviceCheckoutComplete}
            managedPlanService={managedPlanService}
            onPlanAction={manageMysteryPlan}
            onRemoveMysteryPlan={removeMysteryPlan}
          />
        </div>
        <StarsOfShow
          onAdd={addProduct}
          onQuickShop={setQuickShopProduct}
          onViewCart={openCart}
          productQuantities={productQuantities}
        />
        <DeliveryCurtainCall
          selectedDelivery={selectedDelivery}
          cartQuantity={cartQuantity}
          onSelectDelivery={setSelectedDelivery}
          onReviewCart={openCart}
          deliveryLocked={Boolean(mysteryPlanLine)}
        />
        <FounderTrust />
        <TreatFaq />
        <CartReassurance
          cartQuantity={cartQuantity}
          cartTotal={cartTotal}
          onOpenCart={openCart}
        />
      </main>

      <footer className="site-footer treat-footer">
        <div className="treat-footer__media" aria-hidden="true">
          <picture>
            <source
              media="(max-width: 700px)"
              type="image/avif"
              srcSet="/media/higgsfield/editorial/candy-impact-mobile-900x1125.avif"
            />
            <source
              media="(max-width: 700px)"
              srcSet="/media/higgsfield/editorial/candy-impact-mobile-900x1125.webp"
            />
            <source
              type="image/avif"
              srcSet="/media/higgsfield/editorial/candy-impact-desktop-1600x900.avif"
            />
            <img
              className="treat-footer__image"
              src="/media/higgsfield/editorial/candy-impact-desktop-1600x900.webp"
              alt=""
              width="1600"
              height="900"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>

        <div className="treat-footer__brand">
          <a href="#top" aria-label="Back to the Sub Zero Treats home section">
            <BrandMark />
          </a>
          <h2>Freeze-dried favourites made for a little more joy.</h2>
          <p>
            A concept for direct local ordering and scheduled delivery in
            Rockhampton.
          </p>
        </div>

        <nav className="treat-footer__links" aria-label="Footer navigation">
          <a href="#create-a-bucket">Build a bucket</a>
          <a href="#stars-of-the-show">Crowd pleasers</a>
          <a href="#delivery-days">Proposed delivery days</a>
          <a
            href="https://subzerotreats.com.au/"
            target="_blank"
            rel="noreferrer"
          >
            Current online store
            <ExternalLink aria-hidden="true" />
          </a>
          <a
            href="https://www.facebook.com/subzer0treats/"
            target="_blank"
            rel="noreferrer"
          >
            Facebook community
            <ExternalLink aria-hidden="true" />
          </a>
        </nav>

        <p className="treat-footer__fineprint">
          Interactive concept by Penny Wise I.T. No order, payment or delivery
          booking is created.
        </p>
      </footer>

      <AnimatePresence>
        {cartQuantity > 0 && !cartOpen && (
          <motion.button
            className="mobile-cart-bar"
            type="button"
            aria-label={`Open cart, ${cartQuantity} ${
              cartQuantity === 1 ? 'item' : 'items'
            }`}
            onClick={openCart}
            initial={{ y: 90 }}
            animate={{ y: 0 }}
            exit={{ y: 90 }}
          >
            <span>
              View cart · {cartQuantity} {cartQuantity === 1 ? 'item' : 'items'}
            </span>
            <strong>{formatCurrency(cartTotal)}</strong>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div
            className="toast"
            role="status"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <span>{toast}</span>
            <button type="button" onClick={openCart}>
              View cart
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ProductQuickShop
        product={quickShopProduct}
        quantityInCart={
          quickShopProduct
            ? (productQuantities[quickShopProduct.id] ?? 0)
            : 0
        }
        onAdd={addProduct}
        onClose={closeQuickShop}
      />

      <CartDrawer
        open={cartOpen}
        lines={cart}
        delivery={selectedDelivery}
        serviceCheckoutComplete={serviceCheckoutComplete}
        onClose={closeCart}
        onChangeQuantity={changeQuantity}
        onClear={clearCart}
        onCheckoutComplete={(service) => {
          dismissToast()
          if (
            service?.cadence === 'weekly' ||
            service?.cadence === 'fortnightly'
          ) {
            applyManagedPlanService({
              ...service,
              lifecycleStatus: 'active',
            })
            setServiceCheckoutComplete(true)
          } else if (service?.cadence === 'once') {
            setServiceCheckoutComplete(true)
          }
        }}
      />
    </div>
  )
}

export default ShopPage
